<template>
  <v-app>
    <v-expand-transition>
      <v-app-bar
        app
        color="primary white--text"
        v-if="initialized"
        class="d-print-none"
      >
        <div class="w-100 d-flex align-center">
          <h1
            class="font-weight-bold flex-fill text-truncate pr-2"
            :class="{ 'text-h4': !isMobile, 'text-h5': isMobile }"
          >
            {{ surveyTitle }} - {{ responseCount }} Responses
          </h1>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="white"
                @click="dense = !dense"
                icon
                :class="{ 'primary lighten-2': dense }"
                v-on="on"
                v-bind="attrs"
              >
                <v-icon style="font-size: 1.5rem !important">$compact</v-icon>
              </v-btn>
            </template>
            <span>Toggle Compact</span>
          </v-tooltip>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="white" @click="print" icon v-on="on" v-bind="attrs">
                <v-icon style="font-size: 1.5rem !important">$print</v-icon>
              </v-btn>
            </template>
            <span>Print</span>
          </v-tooltip>
        </div>
      </v-app-bar>
    </v-expand-transition>

    <v-main>
      <v-expand-transition>
        <div v-if="initialized" class="d-print-none grey lighten-2">
          <div class="w-100 py-0 d-flex align-center">
            <v-tabs
              v-model="tab"
              grow
              background-color="grey lighten-2"
              show-arrows
            >
              <v-tab
                class="text-no-wrap"
                v-for="report in survey.reports"
                :key="report.id"
                active-class="grey lighten-1"
              >
                <v-icon class="mr-4">${{ report.icon }}</v-icon>
                {{ report.name }}
              </v-tab>
            </v-tabs>
          </div>
        </div>
      </v-expand-transition>

      <div v-if="initialized">
        <div class="mx-4 w-100 mb-8 d-print-block d-none">
          <h1 class="text-h3">
            {{ surveyTitle }}
          </h1>
        </div>
      </div>

      <v-slide-y-transition mode="out-in">
        <template v-for="(report, i) in survey.reports">
          <div
            :key="report.id"
            v-if="initialized && tab === i"
            class="px-4 w-100 pt-8"
          >
            <template v-if="report.type === reportTypes.itemAnalysis">
              <div
                v-for="page in pages.filter((x) =>
                  x.questions.some((y) => !isPrivateQuestion(y))
                )"
                :key="page.key"
              >
                <div v-if="pages.length > 1" class="pt-md-9 pt-4 pb-4 text-h4">
                  {{ page.displayName }}
                </div>
                <template v-for="question in page.questions.filter(x => !isPrivateQuestion(x))">
                  <QuestionTableResponseRate
                    :key="question.key"
                    :survey="survey"
                    :question="question"
                    :dense="dense"
                    :agg="data"
                    v-if="question.type === questionTypes.table"
                  ></QuestionTableResponseRate>
                  <QuestionListResponseRate
                    :key="question.key"
                    :survey="survey"
                    :question="question"
                    :dense="dense"
                    :agg="data"
                    v-else-if="question.type === questionTypes.checklist"
                  ></QuestionListResponseRate>
                  <QuestionResponseRate
                    :key="question.key"
                    :survey="survey"
                    :question="question"
                    :dense="dense"
                    :agg="data"
                    v-else-if="
                      [questionTypes.radio].some((x) => x === question.type)
                    "
                  ></QuestionResponseRate>
                  <text-block
                    ref="pageQuestion"
                    v-else-if="question.type === questionTypes.textBlock"
                    :question="question"
                    :key="question.key"
                    class="mb-16 px-0"
                    text-classes="text-h5"
                  ></text-block>
                </template>
              </div>
            </template>
            <template v-else-if="report.type === reportTypes.agreeAnalysis">
              <div class="px-4 pt-3 pb-4 grey lighten-2 rounded mb-8">
                <div class="text-subtitle-1 font-weight-bold">Report Key</div>
                <v-divider class="mb-2 mt-1"></v-divider>
                <div class="mb-2">
                  The Agreement report reflects the percentage of participants
                  that selected Strongly Agree or Agree for each question.
                </div>
                <div class="d-flex flex-wrap align-center">
                  <v-chip
                    v-for="(code, index) in colorCoding"
                    :key="index"
                    :color="code.classes"
                    class="font-weight-bold mr-2 my-1"
                    >{{ code.label }}</v-chip
                  >
                </div>
              </div>
              <div class="overflow-auto">
                <QuestionComparison
                  :comparison="comparison"
                  :dense="dense"
                  v-if="comparison"
                >
                </QuestionComparison>
                <div
                  v-else
                  v-for="page in pages.filter((x) =>
                    x.questions.some((y) => isWeightedQuestion(y))
                  )"
                  :key="page.key"
                >
                  <div
                    v-if="pages.length > 1"
                    class="pt-md-9 pt-4 pb-4 text-h4"
                  >
                    {{ page.displayName }}
                  </div>
                  <template
                    v-for="question in page.questions.filter(
                      isWeightedQuestion
                    )"
                  >
                    <QuestionTableAgreeRate
                      :key="question.key"
                      :survey="survey"
                      :question="question"
                      :dense="dense"
                      :data="data"
                      v-if="question.type === 'table'"
                    ></QuestionTableAgreeRate>
                    <QuestionAgreeRate
                      :key="question.key"
                      :survey="survey"
                      :question="question"
                      :dense="dense"
                      :data="data"
                      v-else
                    ></QuestionAgreeRate>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </template>
      </v-slide-y-transition>
    </v-main>

    <v-dialog :value="!initialized" persistent width="160">
      <v-card>
        <v-card-text class="py-3 px-4">
          <div class="font-weight-bold text-center mb-1">LOADING RESULTS</div>
          <v-progress-linear
            indeterminate
            height="6"
            color="primary"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import surveyService from "../services/surveyService";
import sortBy from "../utilities/sortBy";

import QuestionAgreeRate from "../components/QuestionReport.AgreeRate.vue";
import QuestionResponseRate from "../components/QuestionReport.ResponseRate.vue";
import QuestionComparison from "../components/QuestionReport.QuestionComparison.vue";
import QuestionTableAgreeRate from "../components/QuestionReport.Table.AgreeRate.vue";
import QuestionTableResponseRate from "../components/QuestionReport.Table.ResponseRate.vue";
import QuestionListResponseRate from "../components/QuestionReport.List.ResponseRate.vue";
import TextBlock from "../components/Survey.Question.TextBlock.vue";

import {
  reportTypes,
  questionTypes,
  surveyStatus,
  accessKeyUseTypes,
  agreeColors,
  questionSettings,
} from "../constants/Survey";

export default {
  name: "PublicReport",
  components: {
    QuestionAgreeRate,
    QuestionResponseRate,
    QuestionListResponseRate,
    QuestionTableAgreeRate,
    QuestionTableResponseRate,
    QuestionComparison,
    TextBlock
  },
  head: {
    title: {
      inner: "Public Report",
      complement: " ",
      separator: " ",
    },
  },
  data: () => ({
    tab: 0,
    pendingStateChanges: 0,
    pageYOffset: 0,
    shrinkThreshold: 70,
    survey: { reports: [] },
    pageIndex: 0,
    initialized: false,
    dense: true,
    aggId: null,
    reports: [],
  }),
  computed: {
    reportTypes() {
      return reportTypes;
    },
    colorCoding() {
      return agreeColors;
    },
    ready() {
      return this.initialized && !this.loading;
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    surveyId() {
      return this.$route.params.id;
    },
    tenantId() {
      return this.$route.params.tenantId;
    },
    surveyTitle() {
      return this.survey?.title ? this.survey?.title + " Results" : "";
    },
    surveyTitleClass() {
      let len = this.surveyTitle.length;
      if (len <= 30) {
        return "text-h3";
      }

      if (len <= 75) {
        return "text-h4";
      }

      if (len <= 150) {
        return "text-h5";
      }

      return "text-h6";
    },
    flattenedQuestions() {
      return this.survey.categories.reduce((arr, category) => {
        category.questions.forEach((question) => {
          if (question.children) {
            question.children.forEach((child) => {
              arr.push({ ...child, options: question.options });
            });
          } else {
            arr.push(question);
          }
        });

        return arr;
      }, []);
    },
    questionTypes() {
      return questionTypes;
    },
    pages() {
      return this.survey ? this.survey.categories : [];
    },
    multiUseCodes() {
      return this.survey?.accessKeyUseType == accessKeyUseTypes.multiple;
    },
    report() {
      return this.reports[this.tab];
    },
    data() {
      if (this.report.type === reportTypes.itemAnalysis) {
        return this.report.aggs[0];
      } else {
        return this.report.aggs;
      }
    },
    comparison() {
      const getQuestion = (key) => {
        return this.flattenedQuestions.find((x) => x.key === key);
      };

      const getAggData = (groupedBy, keys) => {
        var result = null;

        var data = this.data.find((x) => {
          var test1 = JSON.stringify(
            sortBy(
              x.groupedBy.slice().map((x) => {
                return {
                  key: x.key,
                  value: x.value,
                };
              }),
              "key"
            )
          );
          var test2 = JSON.stringify(sortBy(groupedBy, "key"));
          return test1 === test2;
        });

        if (data) {
          keys.forEach((key) => {
            let target = data.results.find((x) => x.key === key);
            if (!target) console.error(`Invalid Key: ${key}`);

            let agg = JSON.parse(JSON.stringify(target));
            if (!result) result = agg;
            else {
              result.responses += agg.responses;
              result.counts = agg.counts.reduce((counts, count) => {
                let i = counts.findIndex((x) => x.key === count.key);

                if (i == -1) counts.push(count);
                else counts[i].value += count.value;

                return counts;
              }, result.counts);
            }
          });
        }

        return result;
      };

      const getColIndex = (groupedBy, columns) => {
        return columns.findIndex((x) => {
          var test1 = JSON.stringify(
            sortBy(
              x.groupedOn.slice().map((x) => {
                return {
                  key: x.key,
                  value: x.value,
                };
              }),
              "key"
            )
          );
          var test2 = JSON.stringify(sortBy(groupedBy, "key"));
          return test1 === test2;
        });
      };

      let result = null;

      if (this.report?.groupComparisons?.length > 0) {
        var columns = this.report.groupComparisons.reduce(
          (arr1, x) => {
            var cols = x.reduce((arr2, y) => {
              var column = {
                text: null,
                groupedOn: y.groupedOn,
              };

              y.groupedOn.forEach((z) => {
                var text = this.flattenedQuestions
                  .find((a) => a.key === z.key)
                  .options.find((a) => a.value === z.value).text;

                if (!column.text) column.text = text;
                else column.text += "<br>" + text;
              });

              if (!arr2.some((z) => z.text === column.text)) arr2.push(column);
              return arr2;
            }, []);

            cols.forEach((y) => {
              if (!arr1.some((z) => z.text === y.text)) arr1.push(y);
            });

            return arr1;
          },
          this.report.showAverage ? [{ text: "All", groupedOn: [] }] : []
        );

        result = {
          columns,
          data: [],
        };

        const emptyColData = {
          numerator: null,
          denominator: null,
          value: null,
        };

        this.report.groupComparisons.slice().forEach((x) => {
          var data = {
            colData: [],
          };

          var average = { numerator: 0, denominator: 0, val: 0 };

          x.forEach((y, i) => {
            var question = getQuestion(y.keys ? y.keys[0] : y.key);
            if (i === 0) {
              data.question = question;
            }

            let agg = getAggData(y.groupedOn, y.keys ?? [y.key]);

            if (agg && question) {
              var numerator = agg.counts.reduce((val, count) => {
                let option = question.options.find((x) => x.value == count.key);
                if (option && option.weight > 0) {
                  val += count.value;
                }

                return val;
              }, 0);

              var denominator = agg.responses;

              var colData = {
                denominator,
                numerator,
                val: !denominator
                  ? null
                  : !numerator
                  ? 0
                  : numerator / denominator,
              };

              let colIndex = getColIndex(y.groupedOn, result.columns);
              data.colData[
                this.report.showAverage ? colIndex - 1 : colIndex
              ] = colData;

              average.numerator += numerator ?? 0;
              average.denominator += denominator ?? 0;
            } else data.colData.push(emptyColData);
          });

          if (this.report.showAverage) {
            average.val = !average.denominator
              ? null
              : !average.numerator
              ? 0
              : average.numerator / average.denominator;

            data.colData.unshift(average);
          }

          // let empties = [];
          // data.colData.forEach((x, i) => {
          //   if (!x) empties.push(i);
          // });
          // empties.forEach((x) => (data.colData[x] = emptyColData));

          result.data.push(data);
        });

        return result;
      }

      return result;
    },
    responseCount() {
      return (
        this.report?.aggs.reduce((agg, item) => item.responseCount + agg, 0) ??
        0
      );
    },
  },
  watch: {
    pageIndex() {
      this.$vuetify.goTo(document.querySelector("#surveySteps"), {
        duration: 0,
      });
    },
  },
  methods: {
    loadSurvey() {
      this.pendingStateChanges++;

      return surveyService
        .get(this.tenantId, this.surveyId)
        .then(
          (response) => {
            response.categories = response.categories.map((x) => {
              x.questions = x.questions.filter(
                (y) =>
                  !y.settings.some(
                    (z) => z.key === "private" && z.value == true
                  )
              );
              return x;
            });
            this.survey = response;
          },
          (error) => {
            this.errorMessage =
              error.errorMessage ?? "An unexpected error occurred.";
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    loadAggs() {
      this.pendingStateChanges++;

      let tasks = this.survey.reports.map((x) => {
        return surveyService.getAggregate(this.tenantId, this.surveyId, x.id);
      });

      return Promise.all(tasks)
        .then(
          (results) => {
            this.reports = this.survey.reports.map((x, i) => {
              results[i].forEach((agg) => {
                agg.groupedBy.forEach((grouping) => {
                  grouping.text = this.flattenedQuestions
                    .find((question) => question.key === grouping.key)
                    ?.options.find((x) => x.value === grouping.value).text;
                });

                agg.results.forEach((x) => {
                  x.responses = x.counts.reduce((prev, c) => {
                    if (c.key != "null") prev += c.value;
                    return prev;
                  }, 0);
                });
              });

              return {
                ...x,
                aggs: results[i],
              };
            });

            this.reports.forEach((agg) => {
              agg;
            });

            // if (aggs.length > 1) {
            //   this.agg = { results: [] };

            // } else if (aggs.length == 0) {
            //   this.agg = {
            //     results: [],
            //   };
            // } else {
            //   this.agg = aggs[0];

            //   this.agg.results.forEach((x) => {
            //     x.responses = x.counts.reduce((prev, c) => {
            //       if (c.key != "null") prev += c.value;
            //       return prev;
            //     }, 0);
            //   });
            // }
          },
          (error) => {
            this.errorMessage =
              error.errorMessage ?? "An unexpected error occurred.";
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    print() {
      let css = `@page { size: ${this.dense ? "landscape" : "portrait"}; }`,
        head = document.head || document.getElementsByTagName("head")[0];

      let styleId = "#print-settings";
      let style = head.querySelector(styleId);

      if (!style) {
        style = document.createElement("style");
        style.setAttribute("id", styleId);
        style.type = "text/css";
        style.media = "print";
        head.appendChild(style);
      }

      if (style.styleSheet) {
        style.styleSheet.cssText = css;
      } else {
        style.appendChild(document.createTextNode(css));
      }

      window.print();
    },
    isWeightedQuestion(question) {
      let result = false;
      if (question.settings) {
        result = question.settings.some(
          (y) =>
            y.key === questionSettings.weightedOptions.key && y.value === "true"
        );
      }

      return result;
    },
    isPrivateQuestion(question) {
      let result = false;
      if (question.settings) {
        result = question.settings.some(
          (y) => y.key === questionSettings.private.key && y.value === "true"
        );
      }

      return result;
    },
  },
  mounted() {
    this.pageYOffset = window.pageYOffset;
    this.$store.commit("setTheme", "adi");

    let favicon = document.querySelector("#favicon");
    favicon.href = "favicon.ico";

    this.loadSurvey().then(
      () => {
        if (
          ![surveyStatus.published.key, surveyStatus.archived.key].some(
            (x) => x === this.survey.status
          )
        ) {
          this.$router.replace({ name: "unavailable" });
        } else {
          this.loadAggs().then(() => (this.initialized = true));
        }
      },
      (err) => {
        console.warn(err);
        this.$router.replace({ name: "error:500" });
      }
    );
  },
};
</script>

<style lang="scss">
html {
  font-size: 1rem;
}

#mainNav {
  &,
  .v-toolbar__content,
  img {
    transition: all 200ms ease-in-out !important;
  }
}

.v-icon.v-icon.overlay-back {
  font-size: 24px !important;
}

.v-input {
  &.text-center input {
    text-align: center;
  }

  input:focus::placeholder {
    color: #fff;
  }
}

.h-100 {
  min-height: 100%;
}
</style>
